// Versão: 1.00
// Data..: 2026-09-26
// Filtro de múltipla seleção (dropdown de checkboxes) reutilizável. Fecha ao clicar
// fora e com ESC; mostra quantos itens estão selecionados e tem atalhos Todos/Limpar.

import { useEffect, useRef, useState } from 'react';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  label: string; // rótulo do botão (ex.: "Dados exibidos")
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  // Largura do painel do dropdown (px). Default: 240.
  menuWidth?: number;
}

export function MultiSelect({ label, options, selected, onChange, menuWidth = 240 }: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Fecha ao clicar fora e com a tecla ESC.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };
  const selectAll = () => onChange(options.map((o) => o.value));
  const clearAll = () => onChange([]);

  const count = selected.length;
  const total = options.length;
  // Texto resumido do botão: todos, nenhum ou "N de M".
  const summary = count === total ? 'Todos' : count === 0 ? 'Nenhum' : `${count} de ${total}`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
      >
        <span>🎛 {label}</span>
        <span className="badge" style={{ fontSize: 11 }}>{summary}</span>
        <span style={{ fontSize: 10, opacity: 0.7 }}>▾</span>
      </button>

      {open && (
        <div
          className="card"
          role="listbox"
          style={{
            position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: menuWidth, maxHeight: 380,
            overflow: 'hidden', padding: 0, zIndex: 50, boxShadow: 'var(--shadow-lg)',
            display: 'flex', flexDirection: 'column',
          }}
        >
          <div className="flex items-center justify-between" style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
            <strong style={{ fontSize: 13 }}>{label}</strong>
            <div className="flex gap-1">
              <button type="button" className="btn btn-sm btn-ghost" style={{ fontSize: 11 }} onClick={selectAll}>Todos</button>
              <button type="button" className="btn btn-sm btn-ghost" style={{ fontSize: 11 }} onClick={clearAll}>Limpar</button>
            </div>
          </div>
          <div style={{ overflowY: 'auto', padding: '6px 0' }}>
            {options.map((o) => (
              <label
                key={o.value}
                className="flex items-center gap-2"
                style={{ padding: '8px 12px', cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(o.value)}
                  onChange={() => toggle(o.value)}
                />
                <span style={{ fontSize: 13 }}>{o.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
